import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatRelativeTime } from '../../../shared/utils/dateUtils';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import EventDetailView from './EventDetailView';
import { AVAILABLE_COLUMNS } from '../config/eventColumns';
import { TABLE_STYLES } from '../styles/dataTableStyles';

const EventRow = ({ event, isExpanded, onToggle, columns }) => {
  const [eventDetails, setEventDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const styles = TABLE_STYLES;

  // Fetch full event details when expanded
  useEffect(() => {
    if (isExpanded && !eventDetails && !loadingDetails) {
      fetchEventDetails();
    }
  }, [isExpanded, event.id]);

  const fetchEventDetails = async () => {
    try {
      setLoadingDetails(true);
      const teamId = localStorage.getItem("selectedTeam");
      
      // Fetch event details - no website_id needed, backend will find by team_id
      const response = await axiosInstance.get(`/events/${event.id}`, {
        params: {
          team_id: teamId
        }
      });
      
      if (response.data.success && response.data.event) {
        setEventDetails(response.data.event);
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      // Fallback to using the event data we already have
      setEventDetails(event);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return formatRelativeTime(date);
    } catch {
      return timestamp;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const truncateSessionId = (sessionId) => {
    if (!sessionId) return 'N/A';
    if (sessionId.length > 20) {
      return `${sessionId.substring(0, 8)}...${sessionId.substring(sessionId.length - 8)}`;
    }
    return sessionId;
  };

  const truncateUserId = (userId) => {
    if (!userId) return 'N/A';
    if (userId.length > 20) {
      return `${userId.substring(0, 8)}...${userId.substring(userId.length - 8)}`;
    }
    return userId;
  };

  const truncateDistinctId = (distinctId) => {
    if (!distinctId) return 'N/A';
    if (distinctId.length > 20) {
      return `${distinctId.substring(0, 8)}...${distinctId.substring(distinctId.length - 8)}`;
    }
    return distinctId;
  };

  const truncateWalletAddress = (address) => {
    if (!address) return 'N/A';
    if (address.length > 20) {
      return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    }
    return address;
  };

  // Render cell content based on column definition
  const renderCellContent = (columnKey) => {
    const columnDef = AVAILABLE_COLUMNS[columnKey];
    if (!columnDef) return 'N/A';

    const field = columnDef.field;
    const fallbackField = columnDef.fallbackField;
    let value = event[field] || (fallbackField ? event[fallbackField] : null);

    // Handle special formatters
    if (columnDef.formatter === 'time') {
      return formatTime(value);
    } else if (columnDef.formatter === 'boolean') {
      if (value === undefined || value === null) return 'N/A';
      return value ? 'Yes' : 'No';
    }

    // Handle truncation for long values
    if (columnKey === 'session_id') {
      return truncateSessionId(value);
    } else if (columnKey === 'distinct_id') {
      return truncateDistinctId(value);
    } else if (columnKey === 'wallet_address') {
      return truncateWalletAddress(value);
    }

    // Default: return value or N/A
    return value || 'N/A';
  };

  // Get alignment class based on column
  const getAlignmentClass = (columnKey) => {
    if (columnKey === 'event_name') {
      return 'text-left';
    }
    return 'text-center';
  };

  // Fallback to default columns if not provided
  const displayColumns = columns && columns.length > 0 
    ? columns 
    : ['event_name', 'session_id', 'distinct_id', 'country', 'os', 'timestamp'];

  return (
    <>
      {/* Event Row */}
      <tr
        onClick={onToggle}
        className="cursor-pointer transition-colors"
        style={{ 
          borderBottom: isExpanded ? 'none' : `1px solid ${styles.borderColor}`,
          backgroundColor: 'transparent'
        }}
        onMouseEnter={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = styles.secondaryBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        {/* Render columns dynamically */}
        {displayColumns.map((columnKey) => {
          const columnDef = AVAILABLE_COLUMNS[columnKey];
          if (!columnDef) return null;

          const alignmentClass = getAlignmentClass(columnKey);
          const isEventName = columnKey === 'event_name';

          return (
            <td key={columnKey} className={`px-6 py-3 whitespace-nowrap ${alignmentClass}`}>
              {isEventName ? (
                <div className="text-sm font-medium flex items-center gap-2" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                  <span>{renderCellContent(columnKey)}</span>
                  {event.event_type === 'auto' ? (
                    <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#e0e7ff', color: '#4338ca' }}>
                      Auto
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                      Custom
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                  {renderCellContent(columnKey)}
                </div>
              )}
            </td>
          );
        })}

        {/* Expand/Collapse Icon */}
        <td className="px-6 py-3 whitespace-nowrap text-right">
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 inline" style={{ color: styles.textPrimary }} />
          ) : (
            <ChevronDown className="h-4 w-4 inline" style={{ color: styles.textSecondary }} />
          )}
        </td>
      </tr>

      {/* Expandable Details Panel */}
      {isExpanded && (
        <tr>
          <td colSpan={displayColumns.length + 1} style={{ borderBottom: `1px solid ${styles.borderColor}`, backgroundColor: 'transparent' }}>
            <div className="px-6 py-4">
              {loadingDetails ? (
                <div className="text-center py-8" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                  Loading event details...
                </div>
              ) : (
                <EventDetailView event={eventDetails || event} />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default EventRow;
